import sum from "lodash/sum";
import { Equal, mapEqual } from "./equal";
import { identity } from "./function";
import { iterableMap } from "./iterator";
import { Key, KeyFn } from "./key";

export function getOrSet<K, V>(
  map: Map<K, V> | (K extends {} ? WeakMap<K, V> : never),
  key: K,
  valueFn: () => V,
): V {
  if (map.has(key)) {
    return map.get(key)!;
  }
  const value = valueFn();
  map.set(key, value);
  return value;
}

export function aggregateBy<K, V, A>(
  iterable: Iterable<V>,
  key: (value: V) => K,
  aggregate: (acc: A, value: V) => A,
  initial: () => A,
  keyFn?: (key: K) => any,
): Map<K, A> {
  const result = keyFn ? new KeyedMap<K, A>(keyFn) : new Map<K, A>();
  for (const value of iterable) {
    const k = key(value);
    const acc = result.has(k) ? result.get(k)! : initial();
    result.set(k, aggregate(acc, value));
  }
  return result;
}

export function countBy<K, V>(
  iterable: Iterable<V>,
  key: (value: V) => K,
  keyFn?: (key: K) => any,
): Map<K, number> {
  return aggregateBy(
    iterable,
    key,
    (acc) => acc + 1,
    () => 0,
    keyFn,
  );
}

/**
 * Combines maps A and B, overwriting any overlaps of the two with B.
 */
export function overwriteCombineMaps<K, V>(
  mapA: Map<K, V>,
  mapB: Map<K, V>,
): Map<K, V> {
  const result = new Map<K, V>(mapA);
  for (const [key, value] of mapB) {
    result.set(key, value);
  }
  return result;
}

export function groupBy<K, V, T = V>(
  iterable: Iterable<V>,
  key: (value: V) => K,
  valueFn: (value: V) => T = identity as (value: V) => T,
): Map<K, T[]> {
  return aggregateBy(
    iterable,
    key,
    (acc, value) => {
      acc.push(valueFn(value));
      return acc;
    },
    () => [] as T[],
  );
}

export function groupMap<T, K, V>(
  iterable: Iterable<T>,
  entry: (value: T) => [K, V] | undefined,
): Map<K, V> {
  const result = new Map<K, V>();
  for (const value of iterable) {
    const entryOpt = entry(value);
    if (!entryOpt) {
      continue;
    }
    const [k, v] = entryOpt;
    result.set(k, v);
  }
  return result;
}

export function reverseMap<K, V>(map: Map<K, V[]>): Map<V, K[]> {
  const result = new Map<V, K[]>();
  for (const [key, values] of map) {
    for (const value of values) {
      getOrSet(result, value, () => []).push(key);
    }
  }
  return result;
}

export function mapMapValues<K, A, B>(
  map: Map<K, A>,
  fn: (value: A, key: K) => B,
): Map<K, B> {
  return new Map(iterableMap(map, ([key, value]) => [key, fn(value, key)]));
}

export function memoize<K, V>(fn: (key: K) => V): (key: K) => V {
  const cache = new Map<K, V>();
  return (key) => getOrSet(cache, key, () => fn(key));
}

export function memoizeRemoveable<K, V>(
  fn: (key: K, remove: () => void) => V,
): (key: K) => V {
  const cache = new Map<K, V>();
  return (key) => getOrSet(cache, key, () => fn(key, () => cache.delete(key)));
}

export class ImmutableCountMap<K> {
  private constructor(private readonly map: Map<K, number>) {}

  get(key: K): number {
    return this.map.get(key) ?? 0;
  }

  increment(key: K, quantity: number = 1): ImmutableCountMap<K> {
    const newMap = new Map(this.map);
    quantity += newMap.get(key) ?? 0;
    if (quantity) {
      newMap.set(key, quantity);
    } else {
      newMap.delete(key);
    }
    return new ImmutableCountMap(newMap);
  }

  sum() {
    return sum([...this.map.values()]);
  }

  [Symbol.iterator](): IterableIterator<[K, number]> {
    return this.map[Symbol.iterator]();
  }

  private static readonly EMPTY = new this(new Map());

  static empty<K, V>(): ImmutableCountMap<K> {
    return this.EMPTY;
  }

  static fromEntries<K>(entries: Iterable<[K, number]>): ImmutableCountMap<K> {
    return new ImmutableCountMap(new Map(entries));
  }

  static fromObject<K extends string, V>(
    object: Record<K, V>,
  ): ImmutableCountMap<K> {
    return this.fromEntries(<[K, number][]>Object.entries(object));
  }

  static of<K>(...entries: [K, number][]): ImmutableCountMap<K> {
    return new ImmutableCountMap(new Map(entries));
  }
}

export class ImmutableMap<K, V> {
  private constructor(private readonly map: Map<K, V>) {}

  get size(): number {
    return this.map.size;
  }

  delete(key: K): ImmutableMap<K, V> {
    const newMap = new Map(this.map);
    newMap.delete(key);
    return new ImmutableMap(newMap);
  }

  entries(): Iterable<[K, V]> {
    return this.map.entries();
  }

  get(key: K): V | undefined {
    return this.map.get(key);
  }

  keys(): Iterable<K> {
    return this.map.keys();
  }

  set(key: K, value: V): ImmutableMap<K, V> {
    const newMap = new Map(this.map);
    newMap.set(key, value);
    return new ImmutableMap(newMap);
  }

  forEach(callbackfn: (value: V, key: K, map: Map<K, V>) => void): void {
    for (const [key, value] of this.map.entries()) {
      callbackfn(value, key, this.map);
    }
  }

  toMap(): Map<K, V> {
    return new Map(this.map);
  }

  values(): Iterable<V> {
    return this.map.values();
  }

  [Symbol.iterator](): IterableIterator<[K, V]> {
    return this.map[Symbol.iterator]();
  }

  private static readonly EMPTY = new this(new Map());

  static empty<K, V>(): ImmutableMap<K, V> {
    return this.EMPTY;
  }

  static fromEntries<K, V>(entries: Iterable<[K, V]>): ImmutableMap<K, V> {
    return new ImmutableMap(new Map(entries));
  }

  static fromObject<K extends string, V>(
    object: Record<K, V>,
  ): ImmutableMap<K, V> {
    return this.fromEntries(<[K, V][]>Object.entries(object));
  }

  static of<K, V>(...entries: [K, V][]): ImmutableMap<K, V> {
    return new ImmutableMap(new Map(entries));
  }
}

export function immutableMapEqual<K, V>(
  valueEquals: Equal<V>,
): Equal<ImmutableMap<K, V>> {
  const equal = mapEqual(valueEquals);
  return (a, b) => equal(a.toMap(), b.toMap());
}

export class KeyedMap<K, V> implements Map<K, V> {
  private readonly data: Map<Key, { key: K; value: V }>;

  constructor(
    private readonly keyFn: KeyFn<K>,
    entries?: Iterable<[K, V]> | null,
  ) {
    this.data = new Map(
      entries &&
        iterableMap(entries, ([key, value]) => [
          this.keyFn(key),
          { key, value },
        ]),
    );
  }

  get size(): number {
    return this.data.size;
  }

  delete(key: K): boolean {
    return this.data.delete(this.keyFn(key));
  }

  clear(): void {
    this.data.clear();
  }

  *entries(): IterableIterator<[K, V]> {
    for (const { key, value } of this.data.values()) {
      yield [key, value];
    }
  }

  forEach(
    callbackfn: (value: V, key: K, map: Map<K, V>) => void,
    thisArg?: any,
  ): void {
    for (const { key, value } of this.data.values()) {
      callbackfn.call(thisArg, value, this.keyFn(key), thisArg);
    }
  }

  get(key: K): V | undefined {
    return this.data.get(this.keyFn(key))?.value;
  }

  has(key: K): boolean {
    return this.data.has(this.keyFn(key));
  }

  *keys(): IterableIterator<K> {
    for (const { key } of this.data.values()) {
      yield key;
    }
  }

  set(key: K, value: V): this {
    this.data.set(this.keyFn(key), { key, value });
    return this;
  }

  *values(): IterableIterator<V> {
    for (const { value } of this.data.values()) {
      yield value;
    }
  }

  [Symbol.iterator](): IterableIterator<[K, V]> {
    return this.entries();
  }

  get [Symbol.toStringTag](): string {
    return "KeyedMap";
  }
}

export function incrementCountMap<K>(
  map: Map<K, number>,
  key: K,
  increment: number = 1,
) {
  const current = map.get(key) ?? 0;
  const next = current + increment;
  map.set(key, next);
}
